import React, { useState } from 'react';
import { simpanNilai, usd, usd2, saat, sebagaiTeks, salin, STATUS_RUN, unjuranKos, CALL_SEHARI } from '../services/promptArchV2Service';

// ═══════════════════════════════════════════════════════════
//  PROMPT ARCHITECTURE V2 — paparan SATU run (Playground, Test History, perbandingan)
//
//  Bentuk output (nama medan JSON) dimiliki server: `binaSkema` / `sahkanOutput` dalam
//  functions/promptArchV2Teras.js. Ubah bentuk di sana → kemas kini pemapar ini.
//  Run yang TIDAK SAH tetap dipaparkan (supaya admin nampak apa yang model pulangkan),
//  tetapi dengan lencana merah + senarai sebab — tidak pernah kelihatan seperti berjaya.
// ═══════════════════════════════════════════════════════════

function ButangSalin({ teks, label = 'Copy' }) {
  const [keadaan, setKeadaan] = useState(null); // null | 'ok' | 'gagal'
  const klik = async () => {
    const ok = await salin(teks);
    setKeadaan(ok ? 'ok' : 'gagal');
    setTimeout(() => setKeadaan(null), 1500);
  };
  return (
    <button onClick={klik} className="text-[11px] font-semibold text-indigo-600 hover:underline whitespace-nowrap">
      {keadaan === 'ok' ? 'Copied' : keadaan === 'gagal' ? 'Gagal salin' : label}
    </button>
  );
}

function BlokPrompt({ label, teks }) {
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 m-0">{label}</p>
        <ButangSalin teks={teks} />
      </div>
      <p className="text-[12.5px] text-slate-700 leading-relaxed m-0 mt-0.5 whitespace-pre-wrap break-words">{teks || '—'}</p>
    </div>
  );
}

function BorangNilai({ run, kriteria, onDisimpan }) {
  const [nilai, setNilai] = useState(() => ({ ...(run.nilai || {}) }));
  const [catatan, setCatatan] = useState(run.catatan || '');
  const [sedang, setSedang] = useState(false);
  const [mesej, setMesej] = useState(null); // { jenis: 'ok' | 'ralat', teks }

  const simpan = async () => {
    setSedang(true);
    setMesej(null);
    try {
      const r = await simpanNilai({ id: run.id, nilai, catatan });
      setMesej({ jenis: 'ok', teks: 'Markah disimpan.' });
      onDisimpan?.(run.id, r.nilai, r.catatan);
    } catch (e) {
      setMesej({ jenis: 'ralat', teks: 'Markah TIDAK disimpan: ' + (e?.message || String(e)) });
    } finally {
      setSedang(false);
    }
  };

  if (!run.id) {
    return <p className="text-[12px] text-amber-700 m-0 mt-3">Run ini tiada dalam Test History, jadi markah tidak boleh disimpan.</p>;
  }
  return (
    <div className="mt-4 pt-3 border-t border-slate-100">
      <p className="text-[12px] font-bold uppercase tracking-wide text-slate-400 m-0 mb-1">Manual rating (1 = lemah · 5 = bagus)</p>
      <p className="text-[11.5px] text-slate-400 m-0 mb-2">Penilaian anda sendiri — tiada skor automatik.</p>
      <div className="space-y-1.5">
        {(kriteria || []).map((k) => (
          <div key={k.k} className="flex items-center justify-between gap-2">
            <span className="text-[12.5px] text-slate-600">{k.label}</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setNilai((p) => ({ ...p, [k.k]: p[k.k] === n ? null : n }))}
                  className={`w-7 h-7 rounded-md text-[12px] font-bold border ${nilai[k.k] === n ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                >{n}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <textarea
        value={catatan}
        onChange={(e) => setCatatan(e.target.value)}
        rows={2}
        maxLength={2000}
        placeholder="Catatan (pilihan) — cth: hook 2 paling kuat, VO scene 3 kaku"
        className="w-full mt-2 px-3 py-2 text-[12.5px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-y"
      />
      <div className="flex items-center gap-3 mt-1.5">
        <button onClick={simpan} disabled={sedang} className="h-8 px-3 text-[12px] font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40">
          {sedang ? 'Menyimpan…' : 'Save rating'}
        </button>
        {mesej && <span className={`text-[12px] ${mesej.jenis === 'ok' ? 'text-emerald-600' : 'text-rose-600'}`}>{mesej.teks}</span>}
      </div>
    </div>
  );
}

export default function ArchV2Hasil({ run, kriteria, onNilaiDisimpan }) {
  if (!run) return null;

  // Panggilan ditolak server SEBELUM OpenAI (input tidak sah / kompilan gagal) — tiada run.
  if (run._ralatPanggilan) {
    return (
      <div className="rounded-xl bg-white border border-rose-200 p-5">
        <p className="text-[14px] font-semibold text-slate-800 m-0">{run._model}</p>
        <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-[12.5px] text-rose-700">
          <span className="font-bold">Tidak dijalankan:</span> {run._ralatPanggilan}
        </div>
      </div>
    );
  }
  if (run._sedang) {
    return (
      <div className="rounded-xl bg-white border border-slate-200 p-8 text-center">
        <span className="material-symbols-outlined text-[28px] text-indigo-500 animate-spin">progressive_activity</span>
        <p className="text-[13px] text-slate-500 m-0 mt-2">{run._model} sedang menulis… biasanya 20 saat – 2 minit.</p>
      </div>
    );
  }

  const st = STATUS_RUN[run.status] || STATUS_RUN.gagal;
  const o = run.output && typeof run.output === 'object' ? run.output : null;
  const scenes = Array.isArray(o?.scenes) ? o.scenes : [];
  const hooks = Array.isArray(o?.hook_options) ? o.hook_options : [];
  const diminta = run.pilihan?.bilBabak;
  const bilOk = scenes.length === diminta;
  const tok = run.token || {};
  const unjuran = run.adaUsage ? unjuranKos(run.kosUSD) : null;

  return (
    <div className={`rounded-xl bg-white p-5 border ${run.status === 'ok' ? 'border-slate-200' : 'border-rose-200'}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[15px] font-semibold text-slate-800 m-0">{run.model}</p>
          <p className="text-[11.5px] text-slate-400 m-0 mt-0.5">
            reasoning {run.reasoning || '—'} · verbosity {run.verbosity || '—'} · max {Number(run.maxTokens || 0).toLocaleString()} token
            {run.vision ? ' · vision ON' : ''}
          </p>
        </div>
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border whitespace-nowrap ${st.kelas}`}>{st.teks}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {[
          ['Latency', saat(run.latencyMs), null],
          ['Token in', run.adaUsage ? Number(tok.input || 0).toLocaleString() : '—', run.adaUsage && tok.cached ? `${Number(tok.cached).toLocaleString()} dari cache` : null],
          ['Token out', run.adaUsage ? Number(tok.output || 0).toLocaleString() : '—', run.adaUsage && tok.reasoning ? `${Number(tok.reasoning).toLocaleString()} reasoning` : null],
          ['Kos 1 call', run.adaUsage ? usd(run.kosUSD) : '—', run.vision ? 'termasuk gambar' : null],
        ].map(([label, nilai, kecil]) => (
          <div key={label} className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[11px] text-slate-400 m-0">{label}</p>
            <p className="text-[13.5px] font-semibold text-slate-800 m-0 mt-0.5">{nilai}</p>
            {kecil && <p className="text-[10.5px] text-slate-400 m-0">{kecil}</p>}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2 text-[11px]">
        <span className={`px-2 py-0.5 rounded font-semibold ${bilOk ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
          scene: {scenes.length} / {diminta ?? '?'} diminta
        </span>
        <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded">structured: {run.modStruktur || '—'}</span>
        {run.finish && <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded">finish: {run.finish}</span>}
        {run.kosUSD == null && run.adaUsage && <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded">harga model ini belum ditetapkan</span>}
        {run.vision && (
          <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-semibold">
            vision: {run.gambar?.jenis === 'data' ? `gambar dimuat naik${run.gambar?.saizKB ? ` (${run.gambar.saizKB} KB)` : ''}` : 'link gambar'}
          </span>
        )}
      </div>

      {unjuran && (
        <p className="text-[12px] text-slate-500 m-0 mt-2">
          Unjuran <b>{CALL_SEHARI.toLocaleString()} call/hari</b> ≈ <b className="text-slate-700">{usd2(unjuran.sehari)} sehari</b> · {usd2(unjuran.sebulan)} sebulan (30 hari) — anggaran ikut run ini, harga semasa makmal.
        </p>
      )}

      {run.disimpan === false && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-[12.5px] text-rose-700">
          <span className="font-bold">Run ini TIDAK tersimpan dalam Test History.</span> {run.ralatSimpan || ''} Salin hasil di bawah sekarang kalau perlu.
        </div>
      )}
      {run.ralat && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-[12.5px] text-rose-700">{run.ralat}</div>
      )}
      {run.ralatSah?.length > 0 && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5">
          <p className="text-[12px] font-bold text-rose-700 m-0 mb-1">Jawapan tidak ikut Output Contract</p>
          <ul className="m-0 pl-4 text-[12.5px] text-rose-700 leading-relaxed list-disc">
            {run.ralatSah.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}
      {(run.notaStruktur || run.amaranSah?.length > 0 || run.amaranKompil?.length > 0) && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
          <p className="text-[12px] font-bold text-amber-800 m-0 mb-1">Perlu dilihat</p>
          <ul className="m-0 pl-4 text-[12.5px] text-amber-800 leading-relaxed list-disc">
            {run.notaStruktur && <li>{run.notaStruktur}</li>}
            {(run.amaranSah || []).map((x, i) => <li key={'s' + i}>{x}</li>)}
            {(run.amaranKompil || []).map((x, i) => <li key={'k' + i}>{x}</li>)}
          </ul>
        </div>
      )}

      {o && (
        <div className="mt-4">
          {sebagaiTeks(o.creative_concept) && (
            <p className="text-[13.5px] text-slate-800 leading-relaxed m-0"><span className="text-slate-400">Konsep:</span> {sebagaiTeks(o.creative_concept)}</p>
          )}
          {hooks.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 m-0 mb-1">Hook options</p>
              <ol className="m-0 pl-5 text-[13px] text-slate-700 leading-relaxed list-decimal">
                {hooks.map((h, i) => <li key={i}>{sebagaiTeks(h)}{i === 0 && <span className="ml-1.5 text-[10.5px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-semibold">dipakai scene 1</span>}</li>)}
              </ol>
            </div>
          )}

          <div className="mt-3 space-y-3">
            {scenes.map((s, i) => {
              const sc = s && typeof s === 'object' ? s : {};
              const syot = Array.isArray(sc.camera_shots) ? sc.camera_shots : [];
              return (
                <div key={i} className="rounded-lg bg-slate-50 px-3.5 py-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[12px] font-bold uppercase tracking-wide text-indigo-500 m-0">
                      Scene {sebagaiTeks(sc.scene_number) || i + 1} · {sebagaiTeks(sc.duration_seconds) || '?'}s
                    </p>
                    <p className="text-[11.5px] text-slate-500 m-0">{sebagaiTeks(sc.narrative_role)}</p>
                  </div>
                  <p className="text-[13.5px] text-slate-800 leading-relaxed m-0 mt-1.5">
                    <span className="text-slate-400">Voiceover:</span> <span className="font-semibold">{sebagaiTeks(sc.voiceover_ms_my) || '—'}</span>
                  </p>
                  {sebagaiTeks(sc.overlay_hook) && (
                    <p className="text-[12.5px] text-slate-600 m-0 mt-1"><span className="text-slate-400">Overlay:</span> {sebagaiTeks(sc.overlay_hook)}</p>
                  )}
                  <BlokPrompt label="Image prompt" teks={sebagaiTeks(sc.image_prompt)} />
                  <BlokPrompt label="Video prompt" teks={sebagaiTeks(sc.video_prompt)} />
                  {syot.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 m-0">Camera shots</p>
                      <ol className="m-0 mt-0.5 pl-5 text-[12.5px] text-slate-600 leading-relaxed list-decimal">
                        {syot.map((x, j) => <li key={j}>{sebagaiTeks(x)}</li>)}
                      </ol>
                    </div>
                  )}
                  {sebagaiTeks(sc.continuity_notes) && (
                    <p className="text-[12px] text-slate-500 leading-relaxed m-0 mt-2"><span className="text-slate-400">Continuity:</span> {sebagaiTeks(sc.continuity_notes)}</p>
                  )}
                </div>
              );
            })}
          </div>

          {(sebagaiTeks(o.caption) || sebagaiTeks(o.cta)) && (
            <div className="mt-3 rounded-lg bg-slate-50 px-3.5 py-3">
              <BlokPrompt label="Caption" teks={sebagaiTeks(o.caption)} />
              <BlokPrompt label="CTA" teks={sebagaiTeks(o.cta)} />
            </div>
          )}
        </div>
      )}

      {run.outputMentah && (
        <details className="mt-3">
          <summary className="text-[12px] font-semibold text-indigo-600 cursor-pointer">Jawapan mentah model{run.outputMentahDipotong ? ' (dipotong untuk simpanan)' : ''}</summary>
          <div className="flex justify-end mt-1"><ButangSalin teks={run.outputMentah} label="Copy raw" /></div>
          <pre className="text-[11.5px] text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0 mt-1 max-h-72 overflow-y-auto">{run.outputMentah}</pre>
        </details>
      )}

      {run.komponen?.length > 0 && (
        <p className="text-[11.5px] text-slate-400 leading-relaxed m-0 mt-3">
          Komponen: {run.komponen.map((c) => `${c.lapisan} ${c.nama}${c.versi != null ? ` v${c.versi}` : ''}`).join(' · ')}
        </p>
      )}

      {(run.status === 'ok' || run.status === 'tidak_sah') && (
        <BorangNilai key={run.id || 'tiada'} run={run} kriteria={kriteria} onDisimpan={onNilaiDisimpan} />
      )}
    </div>
  );
}
